// Shared DOM helpers used by ALL UI modules (toolbar, panels, modals).
// Centralises element creation + the modal mount contract so every module
// produces the same dark-slate look and the same test-visible selectors.
//
// Modal contract (frozen for tests): openModal() mounts a `.modal-overlay`
// containing a `.modal-content`, and ALSO stamps alias classes:
//   - `.modal-backdrop` on the overlay   (test/auth.mjs queries `.modal-backdrop`)
//   - `.modal-title`    on the title span (test/auth.mjs reads `.modal-title`)
// Backdrop click closes the modal. Only one modal is open at a time.

/** Create an element with an optional class and text. */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag)
  if (cls) n.className = cls
  if (text != null) n.textContent = text
  return n
}

export interface ModalOptions {
  /** Heading text (rendered into a `.modal-title` span). */
  title: string
  /** Modal body content — an element, an html string, or a list of nodes. */
  body: HTMLElement | string | (HTMLElement | string)[]
  /** Optional footer action buttons, appended to a flex-end action row. */
  actions?: HTMLElement[]
  /** Optional max-width override for `.modal-content` (e.g. "700px"). */
  width?: string
  /** Optional data-testid stamped on the `.modal-content`. */
  testid?: string
  /** Fired once when the modal is removed for any reason (✕, backdrop, replace). */
  onClose?: () => void
}

let openOverlay: HTMLElement | null = null
let openOnClose: (() => void) | null = null

/** The currently-open modal overlay element, or null. */
export function currentModal(): HTMLElement | null {
  return openOverlay
}

/** Close the open modal (if any). */
export function closeModal(): void {
  openOverlay?.remove()
  openOverlay = null
  const cb = openOnClose
  openOnClose = null
  cb?.()
}

/**
 * Mount a modal into #modalRoot. Returns the overlay element.
 * Replaces any modal already open.
 */
export function openModal(opts: ModalOptions): HTMLElement {
  closeModal()
  const root = document.getElementById('modalRoot') ?? document.body

  // Overlay: live `.modal-overlay` look + `.modal-backdrop` alias for tests.
  const overlay = el('div', 'modal-overlay modal-backdrop')

  const content = el('div', 'modal-content')
  if (opts.width) content.style.maxWidth = opts.width
  if (opts.testid) content.dataset.testid = opts.testid

  // Header: title (with `.modal-title` alias) + close button.
  const header = el('div', 'flex items-center justify-between mb-4')
  const title = el('span', 'modal-title text-base font-bold text-slate-100', opts.title)
  const close = el('button', 'tb-btn', '✕')
  close.setAttribute('aria-label', 'Close')
  close.addEventListener('click', closeModal)
  header.append(title, close)

  const bodyWrap = el('div')
  appendContent(bodyWrap, opts.body)

  content.append(header, bodyWrap)

  if (opts.actions?.length) {
    const actions = el('div', 'flex justify-end gap-2 mt-5')
    actions.append(...opts.actions)
    content.append(actions)
  }

  overlay.append(content)
  overlay.addEventListener('click', (e) => { if (e.target === overlay) closeModal() })
  root.append(overlay)
  openOverlay = overlay
  openOnClose = opts.onClose ?? null
  return overlay
}

function appendContent(host: HTMLElement, content: ModalOptions['body']): void {
  const items = Array.isArray(content) ? content : [content]
  for (const c of items) {
    if (typeof c === 'string') {
      const wrap = el('div')
      wrap.innerHTML = c
      host.append(...Array.from(wrap.childNodes))
    } else {
      host.append(c)
    }
  }
}

/**
 * A numeric stepper widget: `.num-stepper` with − / + buttons around an input.
 * Calls `onChange` with the parsed number on every value change.
 */
export function numStepper(
  value: number,
  onChange: (v: number) => void,
  opts: { min?: number; max?: number; step?: number } = {},
): HTMLElement {
  const { min, max, step = 1 } = opts
  const wrap = el('div', 'num-stepper')
  const input = el('input') as HTMLInputElement
  input.type = 'number'
  input.value = String(value)
  if (min != null) input.min = String(min)
  if (max != null) input.max = String(max)
  input.step = String(step)

  const clamp = (v: number) => {
    if (min != null && v < min) v = min
    if (max != null && v > max) v = max
    return v
  }
  const emit = () => {
    let v = parseFloat(input.value)
    if (Number.isNaN(v)) v = min ?? 0
    v = clamp(v)
    input.value = String(v)
    onChange(v)
  }
  const bump = (dir: number) => {
    const v = clamp((parseFloat(input.value) || 0) + dir * step)
    input.value = String(v)
    onChange(v)
  }

  const dec = el('button', 'num-stepper-btn', '−')
  const inc = el('button', 'num-stepper-btn', '+')
  dec.type = 'button'; inc.type = 'button'
  dec.addEventListener('click', () => bump(-1))
  inc.addEventListener('click', () => bump(1))
  input.addEventListener('change', emit)

  wrap.append(dec, input, inc)
  return wrap
}

export interface DropdownItem {
  label: string
  /** Optional leading icon html (e.g. from ui/icons.ts icon()). */
  iconHtml?: string
  onClick: () => void
}

/**
 * Build a `.dropdown` menu of `.dropdown-item`s positioned under `button`.
 * Returns the menu element (caller appends it next to the button in a
 * `position:relative` wrapper). Clicking an item runs its handler + hides.
 */
export function dropdown(button: HTMLElement, items: DropdownItem[]): HTMLElement {
  const menu = el('div', 'dropdown hidden')
  menu.style.top = '100%'
  menu.style.left = '0'
  for (const it of items) {
    const row = el('div', 'dropdown-item')
    if (it.iconHtml) { const span = el('span'); span.innerHTML = it.iconHtml; row.append(span) }
    row.append(document.createTextNode(it.label))
    row.addEventListener('click', () => { it.onClick(); menu.classList.add('hidden') })
    menu.append(row)
  }
  button.addEventListener('click', (e) => { e.stopPropagation(); menu.classList.toggle('hidden') })
  document.addEventListener('click', (e) => {
    if (!menu.contains(e.target as Node) && e.target !== button) menu.classList.add('hidden')
  })
  return menu
}

let toastEl: HTMLElement | null = null
let toastTimer: ReturnType<typeof setTimeout> | null = null

/** Show a transient toast (legacy showToast, snapshot L6869). */
export function toast(msg: string, ms = 2000): void {
  if (!toastEl) {
    toastEl = el('div', 'toast')
    document.body.append(toastEl)
  }
  toastEl.textContent = msg
  // force reflow so re-triggering re-animates
  void toastEl.offsetWidth
  toastEl.classList.add('show')
  if (toastTimer) clearTimeout(toastTimer)
  toastTimer = setTimeout(() => { toastEl?.classList.remove('show') }, ms)
}
